import { Cancel, Check } from '@mui/icons-material';
import { IconButton, TextField, Typography } from '@mui/material';
import { scaleSequential } from 'd3-scale';
import { interpolateTurbo } from 'd3-scale-chromatic';
import { useEffect, useState } from 'react';
import { getMetricParamName, getMetricUnits } from './RTFOMetricsPanel';
import { getSeismicEventColor } from './RTFOMapWithWellbore';

const RTFOMapLegend = ({
  metricMinMax,
  setMetricMinMax,
  metric,
  darkMode,
  showMetric,
  showSeismic,
  seismicFilter = [],
  setSeismicFilter,
  isBufferZoneHidden,
  setIsBufferZoneHidden,
  isFPZHidden,
  setIsFPZHidden
}) => {
  const [editColorMin, setEditColorMin] = useState(false);
  const [editColorMax, setEditColorMax] = useState(false);
  const [minColorValue, setMinColorValue] = useState(metricMinMax?.[0] || 0);
  const [maxColorValue, setMaxColorValue] = useState(metricMinMax?.[1] || 1);

  // reset min min values when attribute is changed
  useEffect(() => {
    setMinColorValue(metricMinMax?.[0] || 0);
    setMaxColorValue(metricMinMax?.[1] || 1);
  }, [metric, metricMinMax]);

  try {
    if (isNaN(metricMinMax?.[0]) || !isFinite(metricMinMax?.[0]) || isNaN(metricMinMax?.[1]) || !isFinite(metricMinMax?.[1]) || !metric)
      return <></>;

    const colorScale = scaleSequential(interpolateTurbo).domain([metricMinMax?.[0], metricMinMax?.[1]]);

    let grades = [];
    let colors = [];

    const steps = 10;
    const interval = (metricMinMax?.[1] - metricMinMax?.[0]) / steps;
    for (let i = 0; i < steps; i++) {
      const value = metricMinMax?.[0] + i * interval;

      if (!value || isNaN(value)) continue;

      grades.push(value?.toFixed(2));
      colors.push(colorScale(value));
    }

    return (
      <div
        style={{
          width: 180,
          height: 'auto',
          background: !darkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.4)',
          zIndex: 999,
          padding: '10px',
          borderRadius: '5px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
          position: 'absolute',
          right: 10,
          top: 10
        }}
      >
        {showMetric && (
          <div>
            <Typography style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Metrics Legend</Typography>

            {metric !== 'Seismicity Risk' ? 
              grades?.map((grade, i) => {
                return i === 0 && editColorMin ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: 10,
                      cursor: 'pointer'
                    }}
                    key={grade + i}
                  >
                    <TextField
                      size="small"
                      value={minColorValue}
                      style={{ width: 75 }}
                      inputProps={{
                        style: {
                          height: '16px',
                          fontSize: '12px'
                        }
                      }}
                      key={grade + i}
                      onChange={e => setMinColorValue(e.target.value)}
                      onFocus={event => {
                        event.target.select();
                      }}
                    />
                    <IconButton
                      onClick={() => {
                        setMinColorValue(metricMinMax?.[0]);
                        setEditColorMin(false);
                      }}
                    >
                      <Cancel style={{ fontSize: '16px' }} />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        setMetricMinMax(prev => [parseFloat(minColorValue), prev?.[1]]);
                        setEditColorMin(false);
                      }}
                    >
                      <Check style={{ fontSize: '16px' }} />
                    </IconButton>
                  </div>
                ) : i === grades?.length - 1 && editColorMax ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginBottom: 10,
                      cursor: 'pointer'
                    }}
                    key={grade + i}
                  >
                    <TextField
                      size="small"
                      value={maxColorValue}
                      style={{ width: 75 }}
                      inputProps={{
                        style: {
                          height: '16px',
                          fontSize: '12px'
                        }
                      }}
                      key={grade + i}
                      onChange={e => setMaxColorValue(e.target.value)}
                      onFocus={event => {
                        event.target.select();
                      }}
                    />
                    <IconButton
                      onClick={() => {
                        setMaxColorValue(metricMinMax?.[1]);
                        setEditColorMax(false);
                      }}
                    >
                      <Cancel style={{ fontSize: '16px' }} />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        setMetricMinMax(prev => [prev?.[0], parseFloat(maxColorValue)]);
                        setEditColorMax(false);
                      }}
                    >
                      <Check style={{ fontSize: '16px' }} />
                    </IconButton>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      marginBottom: '5px',
                      alignItems: 'center',
                      cursor: i === 0 || i === grades?.length - 1 ? 'pointer' : ''
                    }}
                    key={grade + i}
                    onClick={() => (i === 0 ? setEditColorMin(true) : i === grades?.length - 1 ? setEditColorMax(true) : {})}
                  >
                    <div
                      style={{
                        width: '15px',
                        height: '15px',
                        marginRight: '5px',
                        borderRadius: '3px',
                        boxShadow: '0px 0px 4px lightgrey',
                        backgroundColor: colors?.[i]
                      }}
                    ></div>
                    <Typography
                      style={{ fontSize: '12px' }}
                    >{`${grade}${grades?.[i + 1] ? ' - ' + grades?.[i + 1] : '+'} ${getMetricUnits(getMetricParamName(metric))}`}</Typography>
                  </div>
                );
              }) : 
              [-2, -1, 0, 1, 2, 3, 4].map(value => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      marginBottom: '5px',
                      alignItems: 'center',
                    }}
                    key={value}
                  >
                    <div
                      style={{
                        width: '15px',
                        height: '15px',
                        marginRight: '5px',
                        borderRadius: '3px',
                        boxShadow: '0px 0px 4px lightgrey',
                        backgroundColor: getSeismicityRiskColor(value)
                      }}
                    ></div>
                    <Typography
                      style={{ fontSize: '12px' }}
                    >{`${value} ${getSeismicityRiskLabel(value)}`}</Typography>
                  </div>
                )
              })
            }
            
          </div>
        )}

        {showSeismic && (
          <div style={{ marginTop: 10 }}>
            <Typography style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>Seismicity Legend</Typography>

            {[0.75, 1.5, 2.5, 3.5, 4.5].map(magnitude => {
              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginBottom: '5px',
                    alignItems: 'center',
                    cursor: 'pointer',
                    opacity: seismicFilter.includes(magnitude) ? 0.25 : 1
                  }}
                  onClick={() => {
                    seismicFilter.includes(magnitude)
                      ? setSeismicFilter(prev => prev.filter(d => d !== magnitude))
                      : setSeismicFilter(prev => [...prev, magnitude]);
                  }}
                  key={magnitude}
                >
                  <div
                    style={{
                      width: '15px',
                      height: '15px',
                      marginRight: '5px',
                      borderRadius: '3px',
                      boxShadow: '0px 0px 4px lightgrey',
                      backgroundColor: getSeismicEventColor(magnitude)
                    }}
                  ></div>
                  <Typography style={{ fontSize: '12px' }}>
                    {magnitude === 0.75
                      ? 'Local Magnitude < 1'
                      : magnitude === 1.5
                        ? 'Local Magnitude 1 - 2'
                        : magnitude === 2.5
                          ? 'Local Magnitude 2 - 3'
                          : magnitude === 3.5
                            ? 'Local Magnitude 3 - 4'
                            : magnitude === 4.5
                              ? 'Local Magnitude > 4'
                              : ''}
                  </Typography>
                </div>
              );
            })}

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: '5px',
                alignItems: 'center',
                cursor: 'pointer',
                opacity: isBufferZoneHidden ? 0.25 : 1
              }}
              onClick={() => {
                setIsBufferZoneHidden(prev => !prev);
              }}
            >
              <div
                style={{
                  width: '15px',
                  height: '15px',
                  marginRight: '5px',
                  borderRadius: '3px',
                  border: '2px solid red',
                  boxShadow: '0px 0px 4px lightgrey',
                  backgroundColor: 'transparent'
                }}
              ></div>
              <Typography style={{ fontSize: '12px' }}>Buffer Zone (5 km)</Typography>
            </div>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginBottom: '5px',
            alignItems: 'center',
            cursor: 'pointer',
            opacity: isFPZHidden ? 0.25 : 1
          }}
          onClick={() => {
            setIsFPZHidden(prev => !prev);
          }}
        >
          <div
            style={{
              width: '15px',
              height: '15px',
              marginRight: '5px',
              borderRadius: '3px',
              boxShadow: '0px 0px 4px lightgrey',
              backgroundColor: 'grey'
            }}
          ></div>
          <Typography style={{ fontSize: '12px' }}>FPZ</Typography>
        </div>
      </div>
    );
  } catch (error) {
    console.log(error);
    return <></>;
  }
};

export default RTFOMapLegend;

function getSeismicityRiskLabel(value) {
  switch(value) {
    case -2:
      return 'Out of Target';
    case -1:
      return 'Frac Initiation';
    case 0:
      return 'Very Low';
    case 1:
      return 'Low';
    case 2:
      return 'Moderate 1/Monitor';
    case 3:
      return 'Moderate 2/Monitor';
    case 4:
      return 'High';
    default:
      return '';
  }
}

function getSeismicityRiskColor(value) {
  switch(value) {
    case -2:
      return 'grey';
    case -1:
      return 'purple';
    case 0:
      return 'white';
    case 1:
      return 'yellow';
    case 2:
      return 'peachpuff';
    case 3:
      return 'gold';
    case 4:
      return 'red';
    default:
      return '';
  }
}